import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import { Button, Col, Form, Row } from "react-bootstrap";
import useMonthlySales from "../hooks/useMonthlySales";
import { endOfMonth, getUnixTime, startOfMonth } from "date-fns";
import 'jspdf-autotable';
import { getExpenses, getSales } from "../firebase/firebaseOps";
import { useAuth } from "../context/AuthContext";
import HeaderContainer from "./HeaderContainer";
import FooterContainer from "./FooterContainer";
import './ReportsContainer.css'; // Importa el archivo CSS

const ReportsContainer = () => {
    const data = useMonthlySales(new Date().getMonth(), new Date().getFullYear());
    const { usuario } = useAuth();
    const [startDate, setStartDate] = useState(getUnixTime(startOfMonth(new Date())));
    const [endDate, setEndDate] = useState(getUnixTime(endOfMonth(new Date())));
    const [place, setPlace] = useState('');
    const [places, setPlaces] = useState([]);
    const [includeSales, setIncludeSales] = useState(true);
    const [includeExpenses, setIncludeExpenses] = useState(false);

    useEffect(() => {
        // Obtener los lugares únicos de los datos
        const uniquePlaces = [...new Set(data.map(item => item.place))];
        setPlaces(uniquePlaces);
    }, [data]);

    const handleStartDateChange = async (e) => {
        const newStartDate = getUnixTime(new Date(e.target.value));
        setStartDate(newStartDate);

        const newData = await getSales(new Date(newStartDate * 1000), new Date(endDate * 1000), usuario.uid);
        const uniquePlaces = [...new Set(newData.map(item => item.place))];
        setPlaces(uniquePlaces);
    };

    const handleEndDateChange = async (e) => {
        const newEndDate = getUnixTime(new Date(e.target.value));
        setEndDate(newEndDate);

        const newData = await getSales(new Date(startDate * 1000), new Date(newEndDate * 1000), usuario.uid);
        const uniquePlaces = [...new Set(newData.map(item => item.place))];
        setPlaces(uniquePlaces);
    };

    const handlePlaceChange = (e) => {
        setPlace(e.target.value);
    };

    const handleIncludeSalesChange = (e) => {
        setIncludeSales(e.target.checked);
    };

    const handleIncludeExpensesChange = (e) => {
        setIncludeExpenses(e.target.checked);
    };

    const generatePDF = async () => {
        const salesData = includeSales ? await getSales(new Date(startDate * 1000), new Date(endDate * 1000), usuario.uid) : [];
        const expensesData = includeExpenses ? await getExpenses(startDate * 1000, endDate * 1000, usuario.uid) : [];

        const doc = new jsPDF();
        // Filtrar los datos por rango de fechas
        const filteredSalesData = salesData.filter(item => place === '' || item.place === place).sort((a, b) => a.date - b.date);
        const filteredExpensesData = expensesData.sort((a, b) => a.date - b.date);

        // Título del documento
        doc.setFontSize(18);
        const title = includeSales && includeExpenses? " Ventas y Gastos" : includeSales? "Ventas" : includeExpenses? "Gastos" : "";
        doc.text('Reporte de ' + title, 14, 22);

        doc.setFontSize(12);
        doc.text(`Desde: ${new Date(startDate * 1000).toLocaleDateString()} Hasta: ${new Date(endDate * 1000).toLocaleDateString()}`, 14, 30);

        // Encabezados de la tabla
        let headers = [["Fecha", "Lugar", "Categoria", "Tipo", "Pago", "Monto", "Descripción"]];
        // Datos de la tabla
        const salesRows = filteredSalesData.map(item => [
            new Date(item.date * 1000).toLocaleDateString(), // Convertir Unix Time a fecha legible
            item.place,
            item.type,
            "Venta",
            item.payment,
            item.amount,
            item.description
        ]);

        const expensesRows = filteredExpensesData.map(item => [
            new Date(item.date * 1000).toLocaleDateString(), // Convertir Unix Time a fecha legible
            item.place,
            item.category,
            "Gasto",
            item.amount,
            item.category
        ]);

        const totalSales = filteredSalesData.reduce((acc, item) => acc + item.amount, 0);
        const totalExpenses = filteredExpensesData.reduce((acc, item) => acc + item.amount, 0);

        // Añadir la tabla de ventas al documento
        if (includeSales) {
            headers = [["Fecha", "Lugar", "Categoria", "Tipo", "Monto", "Descripción"]];
            doc.autoTable({
                head: headers,
                body: salesRows,
                startY: 35,
                didDrawPage: (data) => {
                    // Añadir pie de página
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(10);
                    doc.text(`Página ${data.pageNumber} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
                }
            });

            // Mostrar total de ventas
            const finalY = doc.lastAutoTable.finalY;
            doc.setFontSize(12);
            doc.text(`Total Ventas: ${totalSales.toFixed(2)}`, 14, finalY + 10);
        }

        // Añadir la tabla de gastos al documento
        if (includeExpenses) {
            const startY = includeSales ? doc.lastAutoTable.finalY + 20 : 35;
            doc.autoTable({
                head: headers,
                body: expensesRows,
                startY: startY,
                didDrawPage: (data) => {
                    // Añadir pie de página
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(10);
                    doc.text(`Página ${data.pageNumber} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
                }
            });

            const finalY = doc.lastAutoTable.finalY;
            doc.setFontSize(12);
            doc.text(`Total Gastos: ${totalExpenses.toFixed(2)}`, 14, finalY + 10);
        }

        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl);
    };

    return (
        <>
            <HeaderContainer title="Reporte de Ventas" />
            <div>
                <div>
                    <Form.Label>Fecha de Inicio:</Form.Label>
                    <Form.Control type="date" value={new Date(startDate * 1000).toISOString().split('T')[0]} onChange={handleStartDateChange} />
                </div>
                <div>
                    <Form.Label>Fecha de Fin:</Form.Label>
                    <Form.Control type="date" value={new Date(endDate * 1000).toISOString().split('T')[0]} onChange={handleEndDateChange} />
                </div>
                <div>
                    <Form.Label>Lugar:</Form.Label>
                    <Form.Control as="select" value={place} onChange={handlePlaceChange}>
                        <option value="">Todos los lugares</option>
                        {places.map((place, index) => (
                            <option key={index} value={place}>{place}</option>
                        ))}
                    </Form.Control>
                </div>
                <div className="form-group">
                    <Form.Label>Incluir:</Form.Label>
                    <Row>
                        <Col>
                            <Form.Check
                                type="checkbox"
                                label="Ventas"
                                checked={includeSales}
                                onChange={handleIncludeSalesChange}
                                className="custom-checkbox"
                            />
                        </Col>
                        <Col>
                            <Form.Check
                                type="checkbox"
                                className="custom-checkbox"
                                label="Gastos"
                                checked={includeExpenses}
                                onChange={handleIncludeExpensesChange}
                            />
                        </Col>
                    </Row>
                </div>
                <br />
                <Button onClick={generatePDF}>Descargar Reporte PDF</Button>
            </div>
            <FooterContainer />
        </>
    );
}

export default ReportsContainer;
